import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, Lock } from "lucide-react";
import { ElateWordmark } from "../components/elate/ElateLogo";
import { useAuth } from "../lib/auth";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const next = location.state?.from || "/app";

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const result = await login(email, password);
        if (result.ok) navigate(next);
        else setError(result.error);
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden flex flex-col">
            <div className="orb" style={{ top: -120, left: -120, width: 420, height: 420, background: "rgba(0,102,255,0.18)" }} />
            <div className="orb" style={{ bottom: -160, right: -120, width: 520, height: 520, background: "rgba(255,85,0,0.12)" }} />

            <header className="relative z-10 px-5 lg:px-10 py-5">
                <ElateWordmark />
            </header>

            <main className="relative z-10 flex-1 flex items-center justify-center px-5">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-md"
                >
                    <div className="elate-glass rounded-2xl p-8">
                        <div className="mb-7">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Welcome back</div>
                            <h1 className="font-display text-2xl mt-2">Sign in to Elate OS</h1>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-4">
                            <label className="block">
                                <span className="text-xs text-zinc-400 mb-1.5 block">Email</span>
                                <div className="relative">
                                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input
                                        data-testid="login-email"
                                        type="email"
                                        name="email"
                                        autoComplete="username"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-[#0E0E11] border border-white/[0.08] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:border-[#0066FF] outline-none transition"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-xs text-zinc-400 mb-1.5 block">Password</span>
                                <div className="relative">
                                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                    <input
                                        data-testid="login-password"
                                        type="password"
                                        name="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-[#0E0E11] border border-white/[0.08] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:border-[#0066FF] outline-none transition"
                                    />
                                </div>
                            </label>

                            {error && (
                                <div data-testid="login-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                                    {error}
                                </div>
                            )}

                            <button
                                data-testid="login-submit"
                                type="submit"
                                disabled={loading}
                                className="w-full bg-white text-black rounded-xl py-2.5 text-sm font-medium hover:bg-zinc-200 disabled:opacity-50 transition flex items-center justify-center gap-2"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
                            </button>
                        </form>

                        <div className="mt-6 text-center text-xs text-zinc-500">
                            New to Elate?{" "}
                            <Link to="/signup" data-testid="login-go-signup" className="text-white hover:underline">
                                Create an account
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </main>
        </div>
    );
}
